import { ArrowNarrowDownIcon, ArrowNarrowUpIcon, ClockRewindIcon } from '@vizit/icons';
import { ClearableSelect, type ClearableSelectItem } from '@vizit/organisms';
import { CollectionAssetSortOption } from '../collection-asset-grid';
import { CollectionAssetToolbarControl } from './collection-asset-toolbar';

const SORT_ITEMS: ClearableSelectItem[] = [
  {
    id: CollectionAssetSortOption.DateNewest,
    label: 'Newest',
    icon: <ClockRewindIcon size={14} aria-hidden />,
  },
  {
    id: CollectionAssetSortOption.ScoreDesc,
    label: 'Score: high to low',
    icon: <ArrowNarrowDownIcon size={14} aria-hidden />,
  },
  {
    id: CollectionAssetSortOption.ScoreAsc,
    label: 'Score: low to high',
    icon: <ArrowNarrowUpIcon size={14} aria-hidden />,
  },
];

interface CollectionAssetToolbarSortProps {
  /**
   * How to sort assets within the collection
   */
  sortOption: CollectionAssetSortOption;
  /**
   * Called when the user changes the sort option
   */
  onSortOptionChange: (option: CollectionAssetSortOption) => void;
  /**
   * Whether the score sorts are offered. Off while the tiles show a score the list cannot be sorted by, e.g. a
   * benchmark pair's score: the API sorts by each image's own score
   * @default true
   */
  isScoreSortAvailable?: boolean;
}

/**
 * "Sort by:" control for `CollectionAssetToolbar.Controls`.
 *
 * @param props - The component props
 */
function CollectionAssetToolbarSort(props: CollectionAssetToolbarSortProps) {
  const { sortOption, onSortOptionChange, isScoreSortAvailable = true } = props;
  const items = isScoreSortAvailable
    ? SORT_ITEMS
    : SORT_ITEMS.filter((item) => item.id === CollectionAssetSortOption.DateNewest);
  return (
    <CollectionAssetToolbarControl label="Sort by:">
      <ClearableSelect
        ariaLabel="Collection asset sort"
        placeholder="Newest"
        items={items}
        selectedKey={sortOption}
        onSelectionChange={(key) => key && onSortOptionChange(key as CollectionAssetSortOption)}
        includeAllOption={false}
        isClearable={false}
        className="min-w-[200px]"
        data-testid="collection-asset-sort-filter"
      />
    </CollectionAssetToolbarControl>
  );
}

export { CollectionAssetToolbarSort };
export type { CollectionAssetToolbarSortProps };
